const timestamp = (value) => (value == null
  ? NaN
  : new Date(value).getTime());
const normalizeUser = (value) => String(value || '').trim().toLowerCase();

const userAliases = (user, barnUsersMap, includeAliases) => {
  const aliases = [user];
  if (includeAliases) {
    aliases.push(...String(user).split(';'));
    const mapped = barnUsersMap?.[user];
    const users = Array.isArray(mapped)
      ? mapped
      : [mapped];
    users.filter(Boolean).forEach((barnUser) => {
      aliases.push(barnUser.auth0UserId, barnUser.email, barnUser.userFullName);
    });
  }
  return new Set(aliases.map(normalizeUser).filter(Boolean));
};

const matchesAssignment = (assignment, user, barnUsersMap, includeAliases = true) => {
  if (!user) return true;
  const aliases = userAliases(user, barnUsersMap, includeAliases);
  const raw = String(assignment || '');
  const mentions = [...raw.matchAll(/@\[([^\]]*)\]\(([^)]*)\)/g)];
  const values = mentions.length
    ? mentions.flatMap((mention) => [mention[1], mention[2]])
    : raw.split(/[;,]/);
  return values.some((value) => aliases.has(normalizeUser(value)));
};

const matchesTags = (item, tagConditions = []) => {
  const tagIds = new Set((item.tags || []).map((tag) => Number(tag.id)));
  return tagConditions.every((group) => !group?.length || group.some((id) => tagIds.has(Number(id))));
};

const matchesDates = (item, state, collection) => {
  const itemStart = timestamp(item.startDate);
  if (!Number.isFinite(itemStart)) return false;
  const rangeStart = timestamp(state.startDate);
  const rangeEnd = timestamp(state.endDate);
  if (collection === 'incompleteTasks') {
    if (state.boardFilters?.includeIncompleteTasks === false) return false;
    if (state.boardFilters?.includeIncompleteTasks === true && item.completeDate) return false;
    return !Number.isFinite(rangeStart) || itemStart < rangeStart;
  }
  if (Number.isFinite(rangeEnd) && itemStart >= rangeEnd) return false;
  if (collection !== 'schedules') return !Number.isFinite(rangeStart) || itemStart >= rangeStart;
  // Schedule search includes series spanning the range, including a one-second start-boundary tolerance.
  const itemEnd = timestamp(item.endDate);
  return !Number.isFinite(rangeStart) || item.endDate == null || (Number.isFinite(itemEnd) && itemEnd >= rangeStart - 1000);
};

export const matchesBoardItem = (item, state, collection) => {
  if (!item || item.archived || !matchesTags(item, state.tagConditions) || !matchesDates(item, state, collection)) return false;
  const filters = state.boardFilters || {};
  const templateIds = (filters.eventTemplateIds || []).map(Number);
  const isSchedule = collection === 'schedules';
  const workflows = item.workflows || [];
  const itemTemplateIds = isSchedule
    ? workflows.map((workflow) => Number(workflow.eventTemplateId))
    : [Number(item.eventTemplateId || item.eventTemplate?.id)];
  if (templateIds.length && !itemTemplateIds.some((id) => templateIds.includes(id))) return false;

  const barnUsersMap = state.boardBarnUsersMap || {};
  if (isSchedule) {
    const selectedUser = filters.scheduleAssignmentAuth0UserId || filters.auth0UserId;
    return [item.assignment, ...workflows.map((workflow) => workflow.startingAssignment)]
      .some((assignment) => matchesAssignment(assignment, selectedUser, barnUsersMap));
  }
  return matchesAssignment(item.currentEventStatus?.assignment, filters.auth0UserId, barnUsersMap)
    && matchesAssignment(item.scheduleAssignment, filters.scheduleAssignmentAuth0UserId, barnUsersMap, false);
};

export default matchesBoardItem;
